import React, { useState, useEffect } from 'react';
import {
  FileText, Video, Presentation, Eye, Link, ExternalLink,
  Play, BookOpen, Download, Layers, ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCourse } from '../../../../../context/CourseContext';
import { getModuleById } from '../../../../../services/content.service';
import { getCardColor } from '../../../../../utils/courseHelpers';
import {
  card, cardInteractive, microLabel, bodySecondary, cardTitle,
  dividerInner, iconSize, emptyState, motionStaggerChild, motionHoverLift
} from '../../../../../lib/design-system';
import { cn } from '../../../../../lib/utils';

// ─── Content type config ─────────────────────────────────────────────────────

const CONTENT_TYPES = [
  { key: 'pdfs',   label: 'PDFs',           icon: FileText,     countKey: 'pdfCount',   btnLabel: 'View PDF',          btnIcon: Eye },
  { key: 'videos', label: 'Videos',         icon: Video,        countKey: 'videoCount', btnLabel: 'Watch Video',       btnIcon: Play },
  { key: 'ppts',   label: 'Presentations',  icon: Presentation, countKey: 'pptCount',   btnLabel: 'View Presentation', btnIcon: Eye },
  { key: 'links',  label: 'Links',          icon: Link,         countKey: 'linkCount',  btnLabel: 'Open Link',         btnIcon: ExternalLink },
];

// ─── Main Component ──────────────────────────────────────────────────────────

const StudentContentSection = () => {
  const { courseData } = useCourse();
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const modules = courseData?.syllabus?.modules || [];

  useEffect(() => {
    if (modules.length > 0 && !selectedModule) {
      handleModuleSelect(modules[0], 0);
    }
  }, [courseData]);

  const handleModuleSelect = async (module, index) => {
    setSelectedIndex(index);
    setIsLoading(true);
    try {
      const data = await getModuleById(courseData.id, module._id);
      setSelectedModule(data);
    } catch {
      setSelectedModule(module);
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return null;
    const k = 1024;
    const s = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + s[i];
  };

  const formatDate = (d) => {
    if (!d) return null;
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const hasContent = (mod) => CONTENT_TYPES.some(ct => mod?.[ct.key]?.length > 0);
  const activeColor = modules[selectedIndex] ? getCardColor(modules[selectedIndex].moduleTitle) : '#4f46e5';

  return (
    <div className="min-h-[600px]">
      <div className="mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* ── Module Navigation Strip ──────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className={cn(card, 'overflow-hidden')}
        >
          {/* Colored top bar that matches selected module */}
          <div
            className="h-1.5 transition-colors duration-300"
            style={{ backgroundColor: activeColor }}
          />

          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Layers className={cn(iconSize.lg, 'text-gray-400 dark:text-gray-500')} />
                <h2 className={cardTitle}>Course Modules</h2>
              </div>
              <span className={cn(microLabel)}>
                {modules.length} modules
              </span>
            </div>

            {/* Chip grid */}
            <div className="flex flex-wrap gap-2">
              {modules.map((mod, i) => {
                const isActive = selectedIndex === i;
                const color = getCardColor(mod.moduleTitle);
                return (
                  <motion.button
                    key={mod._id}
                    whileHover={{ y: -1, scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleModuleSelect(mod, i)}
                    title={`Module ${mod.moduleNumber}: ${mod.moduleTitle}`}
                    className={cn(
                      'relative w-11 h-11 rounded-sm text-sm font-bold transition-all duration-200 flex items-center justify-center border',
                      isActive
                        ? 'text-white shadow-md border-transparent'
                        : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    )}
                    style={isActive ? {
                      backgroundColor: color,
                      boxShadow: `0 4px 12px ${color}40`,
                    } : {}}
                  >
                    {mod.moduleNumber}
                    {/* Active indicator dot */}
                    {isActive && (
                      <motion.div
                        layoutId="activeChipDot"
                        className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-sm bg-white"
                        style={{ boxShadow: `0 0 4px ${color}` }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* ── Selected Module Content ──────────────────────────────── */}
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="loader"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              {/* Skeleton: Banner */}
              <div className={cn(card, 'overflow-hidden')}>
                <div className="h-24 sm:h-28 bg-gray-200 dark:bg-gray-700 animate-pulse" />
                <div className="p-4 sm:p-5 space-y-3">
                  <div className="h-3 w-48 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
                  <div className="flex gap-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-6 w-20 bg-gray-100 dark:bg-gray-700/60 rounded-sm animate-pulse" />
                    ))}
                  </div>
                </div>
              </div>

              {/* Skeleton: Section header */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-sm bg-gray-200 dark:bg-gray-700 animate-pulse" />
                <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
              </div>

              {/* Skeleton: Card grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className={cn(card, 'overflow-hidden')}>
                    <div className="h-32 sm:h-36 bg-gray-200 dark:bg-gray-700 animate-pulse" />
                    <div className="p-4 space-y-2.5">
                      <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
                      <div className="h-3 w-full bg-gray-100 dark:bg-gray-700/60 rounded-sm animate-pulse" />
                      <div className="h-3 w-2/3 bg-gray-100 dark:bg-gray-700/60 rounded-sm animate-pulse" />
                      <div className="pt-2">
                        <div className="h-9 w-full bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ) : selectedModule ? (
            <motion.div
              key={selectedModule._id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
              className="space-y-6"
            >
              {/* Module Header Banner */}
              <div className={cn(card, 'overflow-hidden')}>
                <div
                  className="relative h-24 sm:h-28 w-full overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${activeColor} 0%, ${activeColor} 60%, ${activeColor}cc 60%, ${activeColor}99 100%)`,
                  }}
                >
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.08) 50%)',
                    }}
                  />
                  {/* Large faded module number */}
                  <span className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 text-6xl sm:text-7xl font-black text-white/15 select-none tracking-tighter">
                    {selectedModule.moduleNumber < 10 ? `0${selectedModule.moduleNumber}` : selectedModule.moduleNumber}
                  </span>
                  {/* Title overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                    <p className="text-white/70 text-xs font-bold uppercase tracking-widest mb-1">
                      Module {selectedModule.moduleNumber}
                    </p>
                    <h2 className="text-lg sm:text-xl font-black tracking-tight text-white drop-shadow-sm">
                      {selectedModule.moduleTitle}
                    </h2>
                  </div>
                </div>

                {/* Description + stats row */}
                <div className="p-4 sm:p-5">
                  {selectedModule.description && (
                    <p className={cn(bodySecondary, 'max-w-3xl mb-4')}>
                      {selectedModule.description}
                    </p>
                  )}
                  {/* Content type summary pills */}
                  <div className="flex flex-wrap gap-2">
                    {CONTENT_TYPES.map(({ key, label, icon: Icon, countKey }) => {
                      const count = selectedModule[countKey] || selectedModule[key]?.length || 0;
                      if (count === 0) return null;
                      return (
                        <div
                          key={key}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300"
                        >
                          <Icon className={iconSize.xs} />
                          <span className="text-xs font-bold">{count}</span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">{label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Content Sections */}
              {!hasContent(selectedModule) ? (
                <div className={cn(emptyState, 'py-16')}>
                  <BookOpen className={cn(iconSize.empty, 'mx-auto text-gray-300 dark:text-gray-600 mb-4')} />
                  <p className="text-sm font-bold text-gray-500 dark:text-gray-400">No content available yet</p>
                  <p className={cn(bodySecondary, 'mt-1')}>Materials will appear here once uploaded</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {CONTENT_TYPES.map(({ key, label, icon: Icon, btnLabel, btnIcon: BtnIcon }) => {
                    const items = selectedModule[key];
                    if (!items || items.length === 0) return null;

                    return (
                      <section key={key}>
                        {/* Section header */}
                        <div className="flex items-center gap-3 mb-4">
                          <div
                            className="w-8 h-8 rounded-sm flex items-center justify-center"
                            style={{ backgroundColor: `${activeColor}15` }}
                          >
                            <Icon className={iconSize.md} style={{ color: activeColor }} />
                          </div>
                          <h3 className={cardTitle}>{label}</h3>
                          <span className="text-xs font-bold rounded-sm px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                            {items.length}
                          </span>
                          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700 ml-2" />
                        </div>

                        {/* Cards grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {items.map((item, idx) => (
                            <ContentCard
                              key={item._id}
                              item={item}
                              contentType={key}
                              btnLabel={btnLabel}
                              BtnIcon={BtnIcon}
                              TypeIcon={Icon}
                              accentColor={activeColor}
                              formatFileSize={formatFileSize}
                              formatDate={formatDate}
                              index={idx}
                            />
                          ))}
                        </div>
                      </section>
                    );
                  })}
                </div>
              )}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ─── Content Card ────────────────────────────────────────────────────────────

const ContentCard = ({
  item, contentType, btnLabel, BtnIcon, TypeIcon,
  accentColor, formatFileSize, formatDate, index
}) => {
  const handleAccess = () => {
    if (contentType === 'links') {
      window.open(item.url || item.link, '_blank');
    } else {
      window.open(item.fileUrl || item.videoUrl, '_blank');
    }
  };

  const size = formatFileSize(item.fileSize);
  const date = formatDate(item.createDate);

  return (
    <motion.div
      className={cn(cardInteractive, 'group flex flex-col')}
      {...motionStaggerChild(index)}
      whileHover={motionHoverLift}
    >
      {/* Thumbnail / colored header */}
      <div className="relative h-32 sm:h-36 bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
        {item.thumbnail?.thumbnailUrl ? (
          <>
            <img
              src={item.thumbnail.thumbnailUrl}
              alt={item.name || item.title}
              className="w-full h-full object-cover"
            />
            {/* Gradient overlay at bottom */}
            <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/30 to-transparent" />
          </>
        ) : (
          <>
            {/* Abstract pattern background */}
            <div
              className="absolute inset-0 opacity-[0.06] dark:opacity-[0.08]"
              style={{
                backgroundImage: `radial-gradient(circle at 20% 80%, ${accentColor} 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${accentColor} 0%, transparent 50%)`,
              }}
            />
            <div className="relative flex flex-col items-center gap-2">
              <TypeIcon size={28} className="text-gray-300 dark:text-gray-500" />
            </div>
          </>
        )}

        {/* Video play overlay */}
        {contentType === 'videos' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="w-10 h-10 rounded-sm flex items-center justify-center bg-white/90 dark:bg-gray-800/90 shadow-md group-hover:scale-105 transition-transform"
            >
              <Play size={18} style={{ color: accentColor }} className="ml-0.5" />
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col p-4">
        <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-1 line-clamp-2">
          {item.name || item.title}
        </h3>

        {(item.description || item.content) && (
          <p className={cn(bodySecondary, 'mb-2 line-clamp-2')}>
            {item.description || item.content}
          </p>
        )}

        {contentType === 'links' && (item.url || item.link) && (
          <p className="text-xs truncate mb-2" style={{ color: accentColor }} title={item.url || item.link}>
            {item.url || item.link}
          </p>
        )}

        {/* Spacer to push footer down */}
        <div className="flex-1" />

        {/* File info */}
        {(size || date) && (
          <div className={cn('flex items-center justify-between mb-3 pt-2 border-t', dividerInner)}>
            <span className={bodySecondary}>{size || ''}</span>
            <span className={bodySecondary}>{date || ''}</span>
          </div>
        )}

        {/* CTA button */}
        <button
          onClick={handleAccess}
          className="w-full py-2.5 rounded-sm text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 text-white"
          style={{
            backgroundColor: accentColor,
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.88'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          <BtnIcon size={15} />
          <span>{btnLabel}</span>
        </button>
      </div>
    </motion.div>
  );
};

export default StudentContentSection;
